import { useMemo, useState } from "react";
import type { AvailabilityCutoffs } from "@/lib/availability/cell-state";
import { groupCell } from "@/lib/availability/groups";
import { cn } from "@/lib/utils";
import { CELL_CLASS, cellText } from "./cell-style";
import { formatHours, fullMonthLabel, STATE_LABEL } from "./labels";
import { type GridGroup, groupMonthTotals } from "./model";
import { possibleMoves, sortMembers, unevenGroup } from "./moves";
import { Note, PanelLabel, PanelShell, Stat } from "./PanelParts";

/** Members listed at first; a big group shows the most over and most free, then more on request. */
const MEMBERS_PAGE = 25;

interface GroupMonthPanelProps {
  group: GridGroup;
  month: string;
  /** "Home Role", "Discipline", "Account", "Manager", or "All Resources". */
  groupByLabel: string;
  cutoffs: AvailabilityCutoffs;
  onSelectResource: (resourceId: string, month: string) => void;
  onClose: () => void;
}

export function GroupMonthPanel({
  group,
  month,
  groupByLabel,
  cutoffs,
  onSelectResource,
  onClose,
}: GroupMonthPanelProps) {
  const [shown, setShown] = useState(MEMBERS_PAGE);
  const result = useMemo(() => groupCell(group.members, month, cutoffs), [group, month, cutoffs]);
  const totals = useMemo(() => groupMonthTotals(group.members, month), [group, month]);
  const members = useMemo(() => sortMembers(group.members, month), [group, month]);
  const moves = useMemo(() => possibleMoves(group.members, month), [group, month]);
  const uneven = unevenGroup(result.state, group.members, month);
  const net = Math.round((totals.availableHours - totals.bookedHours) * 10) / 10;

  return (
    <PanelShell
      label="Group month detail"
      eyebrow={`${fullMonthLabel(month)} · ${groupByLabel}`}
      title={`${group.label} (${group.members.length})`}
      onClose={onClose}
    >
      {totals.planned === 0 ? (
        <Note tone="neutral">
          No one in this group has a plan for this month, so there is nothing to total.
        </Note>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Σ Available" value={`${formatHours(totals.availableHours)} h`} />
          <Stat label="Σ Booked" value={`${formatHours(totals.bookedHours)} h`} />
          <Stat
            label="Net"
            value={net < 0 ? `+${formatHours(-net)} h` : `${formatHours(net)} h free`}
            tone={net < 0 ? "over" : "free"}
          />
        </div>
      )}

      {uneven !== null ? (
        <Note tone="warning">
          <strong>Uneven.</strong> The group is {STATE_LABEL[result.state]}, but {uneven.over}{" "}
          {uneven.over === 1 ? "person is" : "people are"} over while {uneven.room}{" "}
          {uneven.room === 1 ? "has" : "have"} room.
        </Note>
      ) : null}

      <div className="flex flex-col">
        <PanelLabel className="pb-1.5">Members · most over, then most free</PanelLabel>
        {members.slice(0, shown).map((member) => {
          const cell = member.cells[month];
          return (
            <div key={member.resourceId} className="flex items-center gap-2.5 border-t py-1.5">
              <button
                type="button"
                onClick={() => onSelectResource(member.resourceId, month)}
                className="min-w-0 flex-1 cursor-pointer truncate rounded-sm text-left text-sm font-semibold text-heading outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
              >
                {member.name}
              </button>
              {cell === undefined ? (
                <span className="text-xs text-muted-foreground">No data</span>
              ) : (
                <>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {STATE_LABEL[cell.state]}
                  </span>
                  <span
                    className={cn(
                      "flex h-[26px] w-14 shrink-0 items-center justify-center rounded-sm font-mono text-xs font-semibold",
                      CELL_CLASS[cell.state],
                    )}
                  >
                    {cellText(cell)}
                  </span>
                </>
              )}
            </div>
          );
        })}
        {members.length > shown ? (
          <button
            type="button"
            onClick={() => setShown((count) => count + MEMBERS_PAGE)}
            className="cursor-pointer border-t py-2 text-left text-xs font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            Show {Math.min(MEMBERS_PAGE, members.length - shown)} more ({members.length - shown} not
            shown)
          </button>
        ) : null}
      </div>

      {totals.overCount > 0 ? (
        <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
          <PanelLabel className="text-primary">Possible moves inside the group</PanelLabel>
          {moves.length === 0 ? (
            <span className="text-sm text-heading">No one else in this group has room.</span>
          ) : (
            moves.map((move) => (
              <span key={move.from.resourceId} className="text-sm leading-relaxed text-heading">
                {move.from.name} to {move.to.name} · up to {formatHours(move.hours)} h
              </span>
            ))
          )}
          <span className="text-xs text-muted-foreground">
            Nothing moves from here — assign on the Solution&apos;s Pod.
          </span>
        </div>
      ) : null}
    </PanelShell>
  );
}
